import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Translate, withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Button from 'components/atoms/Button';
import Checkbox from 'components/atoms/Checkbox';
import Label from 'components/foundations/Label';
import TextField from 'components/molecules/TextField';
import Datepicker from 'components/widgets/Datepicker';
import Select from 'components/widgets/Select';
import Spinner from 'components/widgets/Spinner';
import { PageHeader, PageWrapper } from 'components/partials';
import { EmptyStateImageSrc, EmptyStateTasks } from 'components/partials/EmptyState/utils';
import { getEmptyStateOrgData } from 'selectors/onboarding';
import { getAdminModeEnabled, getUserData } from 'selectors/user';
import toaster from 'utils/toaster';
import {
    AVAILABLE_FIELDS_LIST,
    CONVERSION_TYPES,
    EXPORT_LOGS_EMPTY_STATE_TASKS,
    EXPORT_TYPE_MAPPING,
    INSTALLS_LOG_TYPE,
    POSTBACKS_LOG_TYPE,
    PRESET_FIELDS_LIST,
    SELECTED_FIELDS_LIST,
    SNOWFLAKE_API_TYPE,
    TRINO_API_TYPE,
} from './constants';
import FieldSelector from './components/FieldSelector';
import FilterTagsRow from './components/FilterTagsRow';
import * as service from './service';
import {
    buildFieldLists,
    buildPreparedQuery,
    buildTagSuggestions,
    canEditDeviceId,
    getAvailableLogTypes,
    getBasicPostbackTypes,
    getConversionTypeLabelKey,
    getDefaultQuery,
    getDeviceIdTypeOptions,
    getDisabledFilters,
    getFieldsList,
    getLogTypeLabelKey,
    getMinDate,
    getOperationOptions,
    getSelectedFieldNames,
    getTaskLink,
    moveFieldBetweenLists,
    optionize,
    resetQueryForLogType,
    setIntersection,
    setSubtraction,
} from './utils';
import css from './ExportLogs.css';

const emptyStateHeaderData = {
    titleText: 'STATIC.EMPTY_STATE.TITLE',
    subTitleText: 'STATIC.EMPTY_STATE.SUB_TITLE.EXPORT_LOGS',
    tasks: EXPORT_LOGS_EMPTY_STATE_TASKS.map(task => ({
        ...task,
        topic:
            task.topic === 'sdk_integrated'
                ? EmptyStateTasks.SDK_INTEGRATED
                : EmptyStateTasks.EXPORT_EVENT_LEVEL_DATA,
    })),
    imageSrc: EmptyStateImageSrc.SINGULAR_DUDE,
};

const selectStyles = {
    control: base => ({ ...base, minHeight: 35 }),
};

const compareAppName = (a, b) => {
    if (a.name > b.name) {
        return 1;
    }
    if (a.name < b.name) {
        return -1;
    }
    return 0;
};

const getResponseError = (response, translate) => {
    if (!response?.value) {
        return translate('STATIC.PAGES.EXPORT_LOGS.UNEXPECTED_ERROR');
    }
    return String(response.value).replace(/"/g, '');
};

const normalizeLimitOption = option => {
    if (!option) {
        return null;
    }
    if (typeof option === 'object') {
        return {
            ...option,
            name: option.name ?? option.value,
            value: option.value ?? option.name,
            label: option.label ?? String(option.name ?? option.value),
        };
    }
    return { name: option, value: option, label: String(option) };
};

function ExportLogsPage({ translate = key => key }) {
    const adminMode = useSelector(getAdminModeEnabled);
    const userData = useSelector(getUserData);
    const emptyStateOrgData = useSelector(getEmptyStateOrgData);

    const operations = useMemo(getOperationOptions, []);
    const defaultOperation = operations[0];

    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState(getDefaultQuery);
    const [showError, setShowError] = useState(false);
    const [daysError, setDaysError] = useState({ visible: false, range: 0 });
    const [taskLink, setTaskLink] = useState('');
    const [exportLoading, setExportLoading] = useState(false);
    const [sqlLoadingType, setSqlLoadingType] = useState(null);
    const [sqlDialog, setSqlDialog] = useState({ open: false, title: '', sql: '' });
    const [fieldsUpdatedDialog, setFieldsUpdatedDialog] = useState({ open: false, fields: [] });

    const [configuration, setConfiguration] = useState({});
    const [apps, setApps] = useState([]);
    const [selected, setSelected] = useState({
        app: null,
        site: null,
        deviceIdType: null,
        postbackType: null,
        limitResult: null,
        partners: [],
        countries: [],
        agencies: [],
        events: [],
        postbackAttributedPartners: [],
    });
    const [partners, setPartners] = useState([]);
    const [events, setEvents] = useState([]);
    const [countries, setCountries] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [deviceIdTypes, setDeviceIdTypes] = useState([]);
    const [postbackTypes, setPostbackTypes] = useState([]);
    const [oldEventTypes, setOldEventTypes] = useState([]);
    const [trinoEventTypes, setTrinoEventTypes] = useState([]);
    const [trinoEventTypesAdminMode, setTrinoEventTypesAdminMode] = useState([]);
    const [apiTypeToMinDate, setApiTypeToMinDate] = useState({});
    const [numOfDaysLimit, setNumOfDaysLimit] = useState(-1);
    const [limitResults, setLimitResults] = useState({ visible: false, default: null, options: [] });
    const [exportFields, setExportFields] = useState(null);
    const [presetTypes, setPresetTypes] = useState({});
    const [showCustomLogExport, setShowCustomLogExport] = useState(false);
    const [realtimeDisabled, setRealtimeDisabled] = useState(false);
    const [filterOperations, setFilterOperations] = useState({
        partners: defaultOperation,
        countries: defaultOperation,
        agencies: defaultOperation,
        events: defaultOperation,
        postbackAttributedPartners: defaultOperation,
    });

    const apsalarEnabled = !!configuration.apsalar_enabled;
    const showEmptyState = !!emptyStateOrgData?.show_export_logs_empty_state;
    const minDate = getMinDate({ userData, apiTypeToMinDate, logType: query.log_type, adminMode });
    const datePickerMinDate = getMinDate({
        userData,
        apiTypeToMinDate,
        logType: query.log_type,
        adminMode,
        allowAdminFullRange: true,
    }) || null;
    const visibleLogTypes = getAvailableLogTypes({
        queryApiType: query.api_type,
        adminMode,
        trinoEventTypes,
        trinoEventTypesAdminMode,
        oldEventTypes,
    });
    const disabledFilters = getDisabledFilters(apsalarEnabled, query.api_type, query.log_type);
    const availableFields = getFieldsList(exportFields, adminMode, query.log_type, AVAILABLE_FIELDS_LIST);
    const selectedFields = getFieldsList(exportFields, adminMode, query.log_type, SELECTED_FIELDS_LIST);
    const presetOptions = presetTypes[EXPORT_TYPE_MAPPING[query.log_type]] || [];
    const showFieldsSelector = query.api_type === TRINO_API_TYPE && (showCustomLogExport || adminMode);
    const deviceInputEnabled = canEditDeviceId(query.log_type);
    const deviceIdTypeOptions = useMemo(
        () => getDeviceIdTypeOptions({ deviceIdTypes, logType: query.log_type, adminMode }),
        [adminMode, deviceIdTypes, query.log_type]
    );

    const partnerSuggestions = useMemo(() => buildTagSuggestions(partners, 'display_name', 'id'), [partners]);
    const countrySuggestions = useMemo(() => buildTagSuggestions(countries, 'name', 'country_code'), [countries]);
    const agencySuggestions = useMemo(() => buildTagSuggestions(agencies, 'display_name', 'agency_id'), [agencies]);
    const eventSuggestions = useMemo(() => {
        const siteId = selected.site?.site_id;
        return buildTagSuggestions(
            events.filter(event => !siteId || event.site_id === siteId),
            'name',
            'id'
        );
    }, [events, selected.site]);
    const timezoneOptions = useMemo(
        () => moment.tz.names().map(timezone => ({ name: timezone, value: timezone, label: timezone })),
        []
    );

    const noIntegratedAppPlaceholder = useMemo(
        () => ({
            name: translate('STATIC.PAGES.EXPORT_LOGS.NO_INTEGRATED_APP'),
            sites: [{ fullName: translate('STATIC.PAGES.EXPORT_LOGS.NO_INTEGRATED_SITE') }],
        }),
        [translate]
    );

    const setDefaultLimitResult = useCallback((limitConfig = limitResults) => {
        let defaultVal = null;
        if (limitConfig.default) {
            defaultVal = normalizeLimitOption(limitConfig.default);
        } else if (limitConfig.options?.length) {
            defaultVal = normalizeLimitOption(limitConfig.options[limitConfig.options.length - 1]);
        }
        setSelected(prev => ({ ...prev, limitResult: defaultVal }));
    }, [limitResults]);

    const markDirty = useCallback(() => {
        setTaskLink('');
    }, []);

    const updateQuery = useCallback(updater => {
        markDirty();
        setQuery(prev => (typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }));
    }, [markDirty]);

    const updateFilterOperation = useCallback((key, operation) => {
        markDirty();
        setFilterOperations(prev => ({ ...prev, [key]: operation }));
    }, [markDirty]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        Promise.all([
            service.getApps(),
            service.getConfiguration(),
            service.getPartners(),
            service.getEvents(),
            service.getCountries(),
            service.getDeviceIdTypes(),
            service.getPostbackTypes(),
            service.getLogFields(),
            service.getAgencies(),
            service.getDefaultExportLogApi(),
            service.getLogTypes(),
        ])
            .then(
                ([
                    appsResponse,
                    configResponse,
                    partnersResponse,
                    eventsResponse,
                    countriesResponse,
                    deviceIdTypesResponse,
                    postbackTypesResponse,
                    fieldsResponse,
                    agenciesResponse,
                    defaultApiResponse,
                    logTypesResponse,
                ]) => {
                    if (!alive) {
                        return;
                    }

                    const sortedApps = [...(appsResponse.apps || [])].sort(compareAppName);
                    const integratedApps = sortedApps.filter(app => app.sites?.some(site => site.is_integrated));
                    const selectedApp = integratedApps[0] || noIntegratedAppPlaceholder;
                    const selectedSite = selectedApp.sites?.[0] || null;
                    const limitConfig = {
                        visible: defaultApiResponse.limit_result_filter?.visible,
                        default: defaultApiResponse.limit_result_filter?.default,
                        options: (defaultApiResponse.limit_result_filter?.options || []).map(option => ({
                            ...normalizeLimitOption(option),
                        })),
                    };
                    const deviceOptions = optionize(deviceIdTypesResponse, 'display_name', 'name');
                    const postbackOptions = optionize(postbackTypesResponse, 'display_name', 'name');
                    const normalizedFields = buildFieldLists(fieldsResponse);

                    setApps(integratedApps);
                    setConfiguration(configResponse.configuration || {});
                    setPartners(partnersResponse);
                    setEvents(eventsResponse);
                    setCountries(countriesResponse);
                    setDeviceIdTypes(deviceOptions);
                    setPostbackTypes(postbackOptions);
                    setAgencies(agenciesResponse);
                    setApiTypeToMinDate(defaultApiResponse.min_export_date || {});
                    setNumOfDaysLimit(defaultApiResponse.num_of_days_limit);
                    setLimitResults(limitConfig);
                    setOldEventTypes(logTypesResponse.old_export_types || []);
                    setTrinoEventTypes(logTypesResponse.new_export_types || []);
                    setTrinoEventTypesAdminMode(logTypesResponse.new_export_types_admin_mode || []);
                    setExportFields(normalizedFields.exportFields);
                    setPresetTypes(normalizedFields.presetTypes);
                    setShowCustomLogExport(!!fieldsResponse.show_custom_log_export);
                    setRealtimeDisabled(!!fieldsResponse.realtime_disabled);
                    setSelected(prev => ({
                        ...prev,
                        app: selectedApp,
                        site: selectedSite,
                        deviceIdType: deviceOptions[0] || null,
                        postbackType: postbackOptions[0] || null,
                        limitResult: limitConfig.default
                            ? normalizeLimitOption(limitConfig.default)
                            : normalizeLimitOption(limitConfig.options[limitConfig.options.length - 1]),
                    }));
                    setQuery(prev => {
                        const defaultDate = moment().format('YYYY-MM-DD');
                        const availableTypes = logTypesResponse.new_export_types || [];
                        const defaultLogType =
                            availableTypes.length && !availableTypes.includes(prev.log_type)
                                ? availableTypes[0]
                                : prev.log_type;
                        return {
                            ...prev,
                            'start-date': defaultDate,
                            'end-date': defaultDate,
                            timezone: configResponse.configuration?.timezone || moment.tz.guess(),
                            api_type: defaultApiResponse.api_type || TRINO_API_TYPE,
                            log_type: defaultLogType,
                            use_realtime: !(fieldsResponse.default_no_realtime_queries || fieldsResponse.realtime_disabled),
                        };
                    });
                    setLoading(false);
                }
            )
            .catch(error => {
                console.error('ExportLogs Screen failed on the following error:', error);
                toaster.error(translate('STATIC.PAGES.EXPORT_LOGS.UNEXPECTED_ERROR_WITH_SUPPORT'));
                setLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [noIntegratedAppPlaceholder, translate]);

    useEffect(() => {
        const onAdminModeChange = event => {
            if (event.detail?.enabled !== undefined) {
                markDirty();
            }
        };
        window.addEventListener('reactAdminModeChanged', onAdminModeChange);
        return () => window.removeEventListener('reactAdminModeChanged', onAdminModeChange);
    }, [markDirty]);

    useEffect(() => {
        const currentDeviceIdType = selected.deviceIdType;
        const hasSelectedOption = deviceIdTypeOptions.some(option => option.name === currentDeviceIdType?.name);
        if (hasSelectedOption || (!currentDeviceIdType && !deviceIdTypeOptions.length)) {
            return;
        }

        setSelected(prev => ({ ...prev, deviceIdType: deviceIdTypeOptions[0] || null }));
    }, [deviceIdTypeOptions, selected.deviceIdType]);

    const updateSelected = updater => {
        markDirty();
        setSelected(prev => (typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }));
    };

    const selectLogType = logType => {
        const nextDeviceIdTypeOptions = getDeviceIdTypeOptions({ deviceIdTypes, logType, adminMode });
        updateQuery(prev => resetQueryForLogType(prev, logType, filterOperations));
        updateSelected(prev => ({
            ...prev,
            partners: [],
            postbackAttributedPartners: [],
            events: [],
            countries: [],
            agencies: [],
            deviceIdType: nextDeviceIdTypeOptions[0] || null,
            postbackType: postbackTypes[0] || null,
            limitResult: null,
        }));
        setDefaultLimitResult();
    };

    const updateFieldList = (listType, patch) => {
        setExportFields(prev => {
            const modeKey = adminMode ? 'adminLists' : 'lists';
            const exportType = EXPORT_TYPE_MAPPING[query.log_type];
            const current = prev?.[modeKey]?.[exportType]?.[listType] || { filter: '', list: [] };
            return {
                ...prev,
                [modeKey]: {
                    ...prev[modeKey],
                    [exportType]: {
                        ...prev[modeKey][exportType],
                        [listType]: {
                            ...current,
                            ...patch,
                        },
                    },
                },
            };
        });
        markDirty();
    };

    const moveField = (field, source, target, targetIndex) => {
        const sourceList = getFieldsList(exportFields, adminMode, query.log_type, source).list;
        const targetList = getFieldsList(exportFields, adminMode, query.log_type, target).list;
        const nextLists = moveFieldBetweenLists({
            sourceList,
            targetList,
            field,
            source,
            target,
            targetIndex,
        });

        if (source === target) {
            updateFieldList(target, { list: nextLists.targetList });
            return;
        }

        updateFieldList(source, { list: nextLists.sourceList });
        updateFieldList(target, { list: nextLists.targetList });
    };

    const clearSelectedFields = () => {
        updateFieldList(AVAILABLE_FIELDS_LIST, {
            list: [...availableFields.list, ...selectedFields.list],
        });
        updateFieldList(SELECTED_FIELDS_LIST, { list: [] });
    };

    const loadPreset = preset => {
        if (!preset) {
            return;
        }
        const exportType = EXPORT_TYPE_MAPPING[query.log_type];
        const presetFields = exportFields?.adminLists?.[exportType]?.[PRESET_FIELDS_LIST]?.[preset.text]?.list || [];
        const presetFieldNames = presetFields.map(field => field.field);
        const allSelectableFields = [...availableFields.list, ...selectedFields.list];
        updateFieldList(AVAILABLE_FIELDS_LIST, {
            list: allSelectableFields.filter(field => !presetFieldNames.includes(field.field)),
        });
        updateFieldList(SELECTED_FIELDS_LIST, { list: presetFields });
    };

    const prepareQuery = () =>
        buildPreparedQuery({
            query,
            selected,
            operations: filterOperations,
            adminMode,
            selectedFields: getSelectedFieldNames(exportFields, adminMode, query.log_type),
        });

    const validateForm = prepared => {
        return !!prepared['start-date'] && !!prepared['end-date'] && !daysError.visible && !!prepared.app && !!prepared.site;
    };

    const handleDateApply = ({ start_date: startDate, end_date: endDate }) => {
        const diff = moment(endDate).diff(moment(startDate), 'days');
        const hasDaysError = numOfDaysLimit !== -1 && diff > numOfDaysLimit;
        setDaysError({ visible: hasDaysError, range: diff });
        updateQuery({
            'start-date': startDate,
            'end-date': endDate,
        });
    };

    const handleRunQuery = prepared => {
        window.mixpanel?.track('Export Logs Page: Run Query', { query: prepared });
        return service.runLogExport(prepared).then(response => {
            if (response.status === 0) {
                const link = getTaskLink({
                    taskId: response.value.task_id,
                    logType: prepared.log_type,
                    apiType: prepared.api_type,
                });
                setTaskLink(link);
                return link;
            }
            toaster.error(getResponseError(response, translate));
            return null;
        });
    };

    const validateAndExecuteQuery = () => {
        if (!query.use_data_censoring) {
            const confirmed = window.confirm(translate('STATIC.PAGES.EXPORT_LOGS.ADMIN_NOT_USING_CENSORING_WARNING_MSG'));
            if (!confirmed) {
                return;
            }
        }

        const prepared = prepareQuery();
        if (!validateForm(prepared)) {
            setShowError(true);
            return;
        }

        setShowError(false);
        const downloadWindow = window.open('about:blank', '_blank');
        if (!downloadWindow) {
            toaster.error(translate('STATIC.PAGES.EXPORT_LOGS.ENABLE_POPUPS_ERROR'));
            return;
        }

        setExportLoading(true);
        service
            .getLogFields()
            .then(fieldsResponse => {
                if (query.api_type === TRINO_API_TYPE && !adminMode) {
                    const exportType = EXPORT_TYPE_MAPPING[query.log_type];
                    const available = fieldsResponse.available_fields?.[exportType] || [];
                    const unsupportedFields = setSubtraction(getSelectedFieldNames(exportFields, adminMode, query.log_type), available);
                    if (unsupportedFields.length) {
                        const supportedFields = setIntersection(
                            getSelectedFieldNames(exportFields, adminMode, query.log_type),
                            available
                        );
                        updateFieldList(SELECTED_FIELDS_LIST, {
                            list: supportedFields.map(field => ({
                                field,
                                display: fieldsResponse.renamed_fields?.[exportType]?.[field] || field,
                            })),
                        });
                        setFieldsUpdatedDialog({
                            open: true,
                            fields: unsupportedFields.map(field => fieldsResponse.renamed_fields?.[exportType]?.[field] || field),
                        });
                        downloadWindow.close();
                        return;
                    }
                }

                return handleRunQuery(prepared)
                    .then(link => {
                        if (link) {
                            downloadWindow.location.href = link;
                            downloadWindow.focus();
                        } else {
                            downloadWindow.close();
                        }
                    })
                    .catch(error => {
                        console.error(error);
                        toaster.error(translate('STATIC.PAGES.EXPORT_LOGS.UNEXPECTED_ERROR'));
                        downloadWindow.close();
                    });
            })
            .catch(error => {
                console.error(error);
                toaster.error(translate('STATIC.PAGES.EXPORT_LOGS.UNEXPECTED_ERROR'));
                downloadWindow.close();
            })
            .finally(() => {
                setExportLoading(false);
            });
    };

    const getSQLQuery = queryApiType => {
        setSqlLoadingType(queryApiType);
        const prepared = {
            ...prepareQuery(),
            query_api_type: queryApiType || query.api_type,
        };
        window.mixpanel?.track('Export Logs Page: Get SQL Query', { query: prepared });
        service
            .getSqlQuery(prepared)
            .then(response => {
                if (response.status === 0) {
                    setSqlDialog({
                        open: true,
                        title: `${prepared.query_api_type} ${translate('STATIC.PAGES.EXPORT_LOGS.SQL_QUERY_TITLE')}`,
                        sql: response.value.sql_query,
                    });
                    return;
                }
                toaster.error(getResponseError(response, translate));
            })
            .catch(error => {
                console.error(error);
                toaster.error(translate('STATIC.PAGES.EXPORT_LOGS.UNEXPECTED_ERROR'));
            })
            .finally(() => {
                setSqlLoadingType(null);
            });
    };

    useEffect(() => {
        window.mixpanel?.track('Export Logs Page: Open');
    }, []);

    if (loading) {
        return (
            <PageWrapper className={css.page}>
                <PageHeader text="STATIC.PAGE_HEADERS.EXPORT_LOGS" />
                <div className={css.loader}>
                    <Spinner show />
                </div>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper className={css.page} allowDropDownPageScrolling>
            <PageHeader
                text="STATIC.PAGE_HEADERS.EXPORT_LOGS"
                subText={{
                    faqLink: 'https://support.singular.net/hc/en-us/articles/360001216992',
                }}
                showEmptyState={showEmptyState}
                showEmptyStateCloseIcon={false}
                emptyStateHeaderData={emptyStateHeaderData}
                emptyStateOrgData={emptyStateOrgData}
            />

            <form className={css.form} onSubmit={event => event.preventDefault()}>
                <div className={css.formRow}>
                    <Label text="STATIC.PAGES.EXPORT_LOGS.DATE" className={css.formLabel} />
                    <div className={css.control}>
                        <Datepicker
                            id="logDatePicker"
                            startDate={query['start-date']}
                            endDate={query['end-date']}
                            minDate={datePickerMinDate}
                            maxDate={moment().tz(configuration.timezone).format('YYYY-MM-DD')}
                            dateLimit={null}
                            onApply={handleDateApply}
                            containerClass={css.datePicker}
                            buttonContainerClass={css.datePickerButton}
                            buttonType="button"
                        />
                    </div>
                </div>

                {daysError.visible && (
                    <div className={css.formRow}>
                        <div className={css.formLabel} />
                        <div className={css.alertDanger}>
                            <Translate
                                id="STATIC.PAGES.EXPORT_LOGS.DAYS_ERROR_MSG"
                                data={{ diff: daysError.range, limit: numOfDaysLimit }}
                            />
                        </div>
                    </div>
                )}

                <div className={css.formRow}>
                    <Label text="STATIC.PAGES.EXPORT_LOGS.TIMEZONE" className={css.formLabel} />
                    <div className={css.control}>
                        {apsalarEnabled ? (
                            <div className={css.primaryTimezone}>
                                <Translate id="STATIC.PAGES.EXPORT_LOGS.PRIMARY_TIMEZONE" />: {query.timezone}
                            </div>
                        ) : (
                            <Select
                                inputId="timezonePicker"
                                value={timezoneOptions.find(option => option.name === query.timezone)}
                                options={timezoneOptions}
                                onChange={option => updateQuery({ timezone: option.name })}
                                styles={selectStyles}
                            />
                        )}
                    </div>
                </div>

                {apsalarEnabled && query.api_type === TRINO_API_TYPE && (showCustomLogExport || adminMode) && !userData.impersonated_org_company && (
                    <div className={css.formRow}>
                        <div className={css.formLabel} />
                        <div className={css.alertWarning}>
                            <Translate id="STATIC.PAGES.EXPORT_LOGS.UNIFIED_EXPORT_WARNING" />{' '}
                            <span className={css.blinkText}>{minDate}</span>
                        </div>
                    </div>
                )}

                {apsalarEnabled && query.api_type === TRINO_API_TYPE && !realtimeDisabled && userData.organization_tier !== 'STANDARD_UNIFIED' && (
                    <div className={css.formRow}>
                        <Label text="STATIC.PAGES.EXPORT_LOGS.CONFIGURATION" className={css.formLabel} />
                        <div className={css.checkboxRow}>
                            <Checkbox
                                checked={!!query.use_realtime}
                                label={<Translate id="STATIC.PAGES.EXPORT_LOGS.USE_REALTIME_DATA" />}
                                labelTooltip="STATIC.PAGES.EXPORT_LOGS.REALTIME_DATA_TOOLTIP"
                                onChange={checked => updateQuery({ use_realtime: checked })}
                            />
                        </div>
                    </div>
                )}

                {apsalarEnabled && query.api_type === TRINO_API_TYPE && adminMode && (
                    <div className={css.formRow}>
                        <Label text="STATIC.PAGES.EXPORT_LOGS.ADMIN_OPTIONS" className={css.formLabel} />
                        <div className={css.checkboxRow}>
                            <Checkbox
                                checked={!!query.use_data_censoring}
                                label={<Translate id="STATIC.PAGES.EXPORT_LOGS.USE_DATA_CENSORING" />}
                                onChange={checked => updateQuery({ use_data_censoring: checked })}
                            />
                        </div>
                    </div>
                )}

                {selected.app && (
                    <>
                        <div className={css.formRow}>
                            <Label text="STATIC.PAGES.EXPORT_LOGS.APP" className={css.formLabel} />
                            <div className={css.control}>
                                <Select
                                    inputId="log-app"
                                    value={selected.app}
                                    options={apps}
                                    getOptionLabel={option => option.name}
                                    getOptionValue={option => String(option.app_id || option.name)}
                                    onChange={app => updateSelected({ app, site: app.sites?.[0] || null })}
                                    styles={selectStyles}
                                />
                            </div>
                        </div>

                        <div className={css.formRow}>
                            <Label text="STATIC.PAGES.EXPORT_LOGS.SITE" className={css.formLabel} />
                            <div className={css.control}>
                                <Select
                                    inputId="log-site"
                                    value={selected.site}
                                    options={selected.app.sites || []}
                                    getOptionLabel={option => option.fullName || option.public_id || option.site_public_id}
                                    getOptionValue={option => String(option.site_id || option.fullName)}
                                    onChange={site => updateSelected({ site })}
                                    styles={selectStyles}
                                />
                            </div>
                        </div>
                    </>
                )}

                <div className={css.formRow}>
                    <Label text="STATIC.PAGES.EXPORT_LOGS.TYPE" className={css.formLabel} />
                    <div className={css.toggleGroup}>
                        {visibleLogTypes.map(logType => (
                            <button
                                key={logType}
                                type="button"
                                className={classNames(css.toggleButton, { [css.active]: query.log_type === logType })}
                                onClick={() => selectLogType(logType)}
                            >
                                <Translate id={getLogTypeLabelKey(logType)} />
                            </button>
                        ))}
                    </div>
                </div>

                {showFieldsSelector && exportFields && (
                    <FieldSelector
                        available={availableFields.list}
                        selected={selectedFields.list}
                        availableFilter={availableFields.filter}
                        selectedFilter={selectedFields.filter}
                        onAvailableFilterChange={filter => updateFieldList(AVAILABLE_FIELDS_LIST, { filter })}
                        onSelectedFilterChange={filter => updateFieldList(SELECTED_FIELDS_LIST, { filter })}
                        onMoveField={moveField}
                        onClearAll={clearSelectedFields}
                        adminMode={adminMode}
                        exportAll={!!query.is_export_admin_mode}
                        onExportAllChange={checked => updateQuery({ is_export_admin_mode: checked })}
                        apsalarEnabled={apsalarEnabled}
                        presetOptions={presetOptions}
                        onLoadPreset={loadPreset}
                        translate={translate}
                    />
                )}

                {(partners.length > 1 || adminMode) && (
                    <FilterTagsRow
                        id="export-log-partners"
                        labelKey={
                            query.log_type === POSTBACKS_LOG_TYPE
                                ? 'STATIC.PAGES.EXPORT_LOGS.POSTBACK_RECIPIENT'
                                : 'STATIC.PAGES.EXPORT_LOGS.PARTNERS'
                        }
                        helpKey={<Translate id="STATIC.PAGES.EXPORT_LOGS.LEAVE_EMPTY_FOR_ALL" />}
                        operation={filterOperations.partners}
                        operations={operations}
                        onOperationChange={operation => updateFilterOperation('partners', operation)}
                        suggestions={partnerSuggestions}
                        tags={selected.partners}
                        placeholder="STATIC.PAGES.EXPORT_LOGS.PLACEHOLDERS.ADD_PARTNERS"
                        disabled={disabledFilters.partners}
                        onChange={tags => updateSelected({ partners: tags || [] })}
                    />
                )}

                {apsalarEnabled && query.api_type === TRINO_API_TYPE && (
                    <FilterTagsRow
                        id="export-log-countries"
                        labelKey="STATIC.PAGES.EXPORT_LOGS.COUNTRIES"
                        helpKey={<Translate id="STATIC.PAGES.EXPORT_LOGS.LEAVE_EMPTY_FOR_ALL" />}
                        operation={filterOperations.countries}
                        operations={operations}
                        onOperationChange={operation => updateFilterOperation('countries', operation)}
                        suggestions={countrySuggestions}
                        tags={selected.countries}
                        placeholder="STATIC.PAGES.EXPORT_LOGS.PLACEHOLDERS.ADD_COUNTRIES"
                        disabled={disabledFilters.countries}
                        onChange={tags => updateSelected({ countries: tags || [] })}
                    />
                )}

                {apsalarEnabled && agencies.length > 0 && (
                    <FilterTagsRow
                        id="export-log-agencies"
                        labelKey="STATIC.PAGES.EXPORT_LOGS.AGENCIES"
                        helpKey={<Translate id="STATIC.PAGES.EXPORT_LOGS.LEAVE_EMPTY_FOR_ALL" />}
                        operation={filterOperations.agencies}
                        operations={operations}
                        onOperationChange={operation => updateFilterOperation('agencies', operation)}
                        suggestions={agencySuggestions}
                        tags={selected.agencies}
                        placeholder="STATIC.PAGES.EXPORT_LOGS.PLACEHOLDERS.ADD_AGENCIES"
                        disabled={disabledFilters.agencies}
                        onChange={tags => updateSelected({ agencies: tags || [] })}
                    />
                )}

                <FilterTagsRow
                    id="export-log-events"
                    labelKey="STATIC.PAGES.EXPORT_LOGS.EVENTS"
                    helpKey={<Translate id="STATIC.PAGES.EXPORT_LOGS.LEAVE_EMPTY_FOR_ALL" />}
                    operation={filterOperations.events}
                    operations={operations}
                    onOperationChange={operation => updateFilterOperation('events', operation)}
                    suggestions={eventSuggestions}
                    tags={selected.events}
                    placeholder="STATIC.PAGES.EXPORT_LOGS.PLACEHOLDERS.ADD_EVENTS"
                    disabled={disabledFilters.events}
                    onChange={tags => updateSelected({ events: tags || [] })}
                />

                {apsalarEnabled && query.api_type === TRINO_API_TYPE && query.log_type === INSTALLS_LOG_TYPE && (
                    <div className={css.formRow}>
                        <Label text="STATIC.PAGES.EXPORT_LOGS.CONVERSION_TYPE" className={css.formLabel} />
                        <div className={css.toggleGroup}>
                            {CONVERSION_TYPES.map(conversionType => (
                                <button
                                    key={conversionType}
                                    type="button"
                                    className={classNames(css.toggleButton, {
                                        [css.active]: query.conversion_type_filter === conversionType,
                                    })}
                                    onClick={() => updateQuery({ conversion_type_filter: conversionType })}
                                >
                                    <Translate id={getConversionTypeLabelKey(conversionType)} />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {apsalarEnabled && query.api_type === TRINO_API_TYPE && (
                    <>
                        <div className={css.formRow}>
                            <div className={classNames(css.formLabel, css.filterLabel)}>
                                <Label text="STATIC.PAGES.EXPORT_LOGS.DEVICE_ID" className={css.formLabelText} />
                                <span>
                                    <Translate id="STATIC.PAGES.EXPORT_LOGS.LEAVE_EMPTY_FOR_ALL" />
                                </span>
                            </div>
                            <div className={css.control}>
                                <TextField
                                    inputId="export-log-device-id"
                                    containerClass={css.textField}
                                    inputClassName={css.textInput}
                                    value={query.device_id || ''}
                                    placeholder="STATIC.PAGES.EXPORT_LOGS.PLACEHOLDERS.DEVICE_ID"
                                    disabled={!deviceInputEnabled}
                                    onChange={deviceId => updateQuery({ device_id: deviceId })}
                                />
                            </div>
                        </div>

                        <div className={css.formRow}>
                            <Label text="STATIC.PAGES.EXPORT_LOGS.DEVICE_TYPE" className={css.formLabel} />
                            <div className={css.control}>
                                <Select
                                    inputId="log-device-id-type"
                                    value={selected.deviceIdType}
                                    options={deviceIdTypeOptions}
                                    getOptionLabel={option => option.display_name}
                                    getOptionValue={option => option.name}
                                    isDisabled={!deviceInputEnabled}
                                    onChange={deviceIdType => updateSelected({ deviceIdType })}
                                    styles={selectStyles}
                                />
                            </div>
                        </div>
                    </>
                )}

                {apsalarEnabled && query.api_type === TRINO_API_TYPE && query.log_type === POSTBACKS_LOG_TYPE && (
                    <>
                        <div className={css.formRow}>
                            <Label text="STATIC.PAGES.EXPORT_LOGS.POSTBACK_TYPE" className={css.formLabel} />
                            <div className={css.control}>
                                <Select
                                    inputId="log-postback-type"
                                    value={selected.postbackType}
                                    options={adminMode ? postbackTypes : getBasicPostbackTypes(postbackTypes)}
                                    getOptionLabel={option => option.display_name}
                                    getOptionValue={option => option.name}
                                    onChange={postbackType => updateSelected({ postbackType })}
                                    styles={selectStyles}
                                />
                            </div>
                        </div>

                        <FilterTagsRow
                            id="log-postback-attributed-partner"
                            labelKey="STATIC.PAGES.EXPORT_LOGS.ATTRIBUTED_PARTNERS"
                            helpKey={<Translate id="STATIC.PAGES.EXPORT_LOGS.LEAVE_EMPTY_FOR_ALL" />}
                            operation={filterOperations.postbackAttributedPartners}
                            operations={operations}
                            onOperationChange={operation =>
                                updateFilterOperation('postbackAttributedPartners', operation)
                            }
                            suggestions={partnerSuggestions}
                            tags={selected.postbackAttributedPartners}
                            placeholder="STATIC.PAGES.EXPORT_LOGS.PLACEHOLDERS.ADD_PARTNERS"
                            onChange={tags => updateSelected({ postbackAttributedPartners: tags || [] })}
                        />
                    </>
                )}

                {apsalarEnabled && adminMode && query.api_type === TRINO_API_TYPE && (
                    <div className={css.formRow}>
                        <div className={classNames(css.formLabel, css.filterLabel)}>
                            <Label text="STATIC.PAGES.EXPORT_LOGS.ORIGINAL_RECORD" className={css.formLabelText} />
                            <span>
                                <Translate id="STATIC.PAGES.EXPORT_LOGS.LEAVE_EMPTY_FOR_ALL" />
                            </span>
                        </div>
                        <div className={css.control}>
                            <TextField
                                inputId="export-log-like"
                                containerClass={css.textField}
                                inputClassName={css.textInput}
                                value={query.text_search || ''}
                                placeholder="STATIC.PAGES.EXPORT_LOGS.PLACEHOLDERS.TEXT_SEARCH"
                                onChange={textSearch => updateQuery({ text_search: textSearch })}
                            />
                        </div>
                    </div>
                )}

                {((apsalarEnabled && query.api_type === TRINO_API_TYPE) || limitResults.visible) && (
                    <div className={css.formRow}>
                        <Label text="STATIC.PAGES.EXPORT_LOGS.LIMIT" className={css.formLabel} />
                        <div className={css.control}>
                            <Select
                                inputId="log-limit-results"
                                value={selected.limitResult}
                                options={limitResults.options}
                                getOptionLabel={option => String(option.name)}
                                getOptionValue={option => String(option.name)}
                                onChange={limitResult => updateSelected({ limitResult })}
                                styles={selectStyles}
                            />
                        </div>
                    </div>
                )}

                <hr className={css.divider} />
                <div className={css.actionsRow}>
                    {showError && (
                        <div className={css.alertDanger}>
                            <Translate id="STATIC.PAGES.EXPORT_LOGS.ERROR_MSG" />
                        </div>
                    )}
                    {adminMode && (
                        <>
                            <Button
                                id="get-sql-button"
                                className={css.primaryButton}
                                disabled={!query['start-date'] || !query['end-date'] || !!sqlLoadingType}
                                showSpinner={sqlLoadingType === TRINO_API_TYPE}
                                onClick={() => getSQLQuery(TRINO_API_TYPE)}
                                text="STATIC.PAGES.EXPORT_LOGS.GET_SQL_QUERY"
                            />
                            <Button
                                id="get-snowflake-sql-button"
                                className={css.primaryButton}
                                disabled={!query['start-date'] || !query['end-date'] || !!sqlLoadingType}
                                showSpinner={sqlLoadingType === SNOWFLAKE_API_TYPE}
                                onClick={() => getSQLQuery(SNOWFLAKE_API_TYPE)}
                                text="STATIC.PAGES.EXPORT_LOGS.GET_SNOWFLAKE_SQL_QUERY"
                            />
                        </>
                    )}
                    {!taskLink ? (
                        <Button
                            id="export-button"
                            className={css.primaryButton}
                            disabled={showEmptyState || !query['start-date'] || !query['end-date'] || exportLoading}
                            showSpinner={exportLoading}
                            onClick={validateAndExecuteQuery}
                            text="STATIC.PAGES.EXPORT_LOGS.EXPORT"
                        />
                    ) : (
                        <a className={css.followButton} href={taskLink} target="_blank" rel="noreferrer">
                            <Translate id="STATIC.PAGES.EXPORT_LOGS.FOLLOW_YOUR_REQUEST" />
                        </a>
                    )}
                </div>
            </form>

            <Dialog
                open={sqlDialog.open}
                onClose={() => setSqlDialog({ open: false, title: '', sql: '' })}
                maxWidth="md"
                fullWidth
                disablePortal
            >
                <DialogTitle>{sqlDialog.title}</DialogTitle>
                <DialogContent>
                    <pre className={css.sqlPre}>{sqlDialog.sql}</pre>
                </DialogContent>
                <DialogActions>
                    <Button
                        className={css.primaryButton}
                        onClick={() => setSqlDialog({ open: false, title: '', sql: '' })}
                        text="STATIC.BUTTONS.CLOSE"
                    />
                </DialogActions>
            </Dialog>

            <Dialog
                open={fieldsUpdatedDialog.open}
                onClose={() => setFieldsUpdatedDialog({ open: false, fields: [] })}
                maxWidth="sm"
                fullWidth
                disablePortal
            >
                <DialogTitle>
                    <Translate id="STATIC.PAGES.EXPORT_LOGS.AVAILABLE_FIELDS_CHANGED" />
                </DialogTitle>
                <DialogContent>
                    <Translate id="STATIC.PAGES.EXPORT_LOGS.FIELDS_UPDATED" />
                    <ul className={css.noBullet}>
                        {fieldsUpdatedDialog.fields.map(field => (
                            <li key={field}>{field}</li>
                        ))}
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button
                        className={css.primaryButton}
                        onClick={() => setFieldsUpdatedDialog({ open: false, fields: [] })}
                        text="STATIC.BUTTONS.CLOSE"
                    />
                </DialogActions>
            </Dialog>
        </PageWrapper>
    );
}

ExportLogsPage.propTypes = {
    translate: PropTypes.func,
};

export default withLocalize(ExportLogsPage);
